import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import {
  MaterialCategory,
  MarketingMaterial,
  FileTypeFilter,
  SortOption,
  formatFileSize,
} from '../../types/marketing-materials';
import MaterialCard from './MaterialCard';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface MaterialsLibraryProps {
  userId: number;
}

const MaterialsLibrary: React.FC<MaterialsLibraryProps> = ({ userId: _userId }) => {
  const [categories, setCategories] = useState<MaterialCategory[]>([]);
  const [materials, setMaterials] = useState<MarketingMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState<FileTypeFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');

  // Preview modal state
  const [previewMaterial, setPreviewMaterial] = useState<MarketingMaterial | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadCategories(), loadMaterials()]);
    } catch (err: any) {
      setError(err.message || 'Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    const response = await fetch(`${API_BASE_URL}/api/user/materials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify({ operation: 'load_categories' })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to load categories');
    setCategories(result.data?.categories || []);
  };

  const loadMaterials = async () => {
    const response = await fetch(`${API_BASE_URL}/api/user/materials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify({ operation: 'load_materials' })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to load materials');
    setMaterials(result.data?.materials || []);
  };

  // Filter and sort materials
  const filteredMaterials = materials
    .filter(material => {
      // Category filter
      if (selectedCategory && material.category_id !== selectedCategory) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!material.title.toLowerCase().includes(query) &&
            !material.description?.toLowerCase().includes(query)) {
          return false;
        }
      }

      // File type filter
      if (fileTypeFilter !== 'all') {
        if (fileTypeFilter === 'images' && !material.file_type.startsWith('image/')) return false;
        if (fileTypeFilter === 'pdfs' && material.file_type !== 'application/pdf') return false;
        if (fileTypeFilter === 'documents' && !material.file_type.includes('officedocument')) return false;
      }

      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'name-asc':
          return a.title.localeCompare(b.title);
        case 'name-desc':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

  // Get material count per category
  const getCategoryCount = (categoryId: string) => {
    return materials.filter(m => m.category_id === categoryId && m.is_published).length;
  };

  // Handle download
  const handleDownload = async (material: MarketingMaterial) => {
    try {
      // Get signed URL for download
      const urlResponse = await fetch(`${API_BASE_URL}/api/user/materials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'get_download_url', data: { filePath: material.file_url } })
      });
      const urlResult = await urlResponse.json();
      if (!urlResponse.ok) throw new Error(urlResult.error || 'Failed to get download URL');

      // Increment download count
      await fetch(`${API_BASE_URL}/api/user/materials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'increment_download_count', data: { materialId: material.id } })
      });

      // Trigger download
      const link = document.createElement('a');
      link.href = urlResult.data?.signedUrl;
      link.download = material.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Reload materials to update download count
      await loadMaterials();
    } catch (err: any) {
      setError(err.message || 'Failed to download file');
    }
  };

  // Handle preview
  const handlePreview = async (material: MarketingMaterial) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/materials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'get_preview_url', data: { filePath: material.file_url } })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to get preview URL');

      setPreviewUrl(result.data?.signedUrl);
      setPreviewMaterial(material);
      setShowPreviewModal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load preview');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#ffffff' }}>
        <div style={{ fontSize: '18px' }}>Loading materials...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#ffffff', marginBottom: '10px' }}>
          Marketing Materials
        </h1>
        <p style={{ color: '#ffffff', fontSize: '14px' }}>
          Download company-approved marketing materials and assets
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          marginBottom: '20px',
          color: '#f87171',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '18px' }}>×</button>
        </div>
      )}

      {/* Category Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '25px',
        overflowX: 'auto',
        paddingBottom: '10px',
        borderBottom: '1px solid #374151',
      }}>
        <button
          onClick={() => setSelectedCategory(null)}
          style={{
            padding: '10px 20px',
            background: 'none',
            border: 'none',
            borderBottom: selectedCategory === null ? '2px solid #d4af37' : '2px solid transparent',
            color: selectedCategory === null ? '#d4af37' : '#9ca3af',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: selectedCategory === null ? 'bold' : 'normal',
            whiteSpace: 'nowrap',
          }}
        >
          All Materials ({materials.filter(m => m.is_published).length})
        </button>
        {categories.map(category => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            style={{
              padding: '10px 20px',
              background: 'none',
              border: 'none',
              borderBottom: selectedCategory === category.id ? '2px solid #d4af37' : '2px solid transparent',
              color: selectedCategory === category.id ? '#d4af37' : '#9ca3af',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: selectedCategory === category.id ? 'bold' : 'normal',
              whiteSpace: 'nowrap',
            }}
          >
            {category.name} ({getCategoryCount(category.id)})
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '25px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search materials..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1,
            minWidth: '200px',
            padding: '12px',
            backgroundColor: 'rgba(55, 65, 81, 0.5)',
            border: '1px solid #4b5563',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
          }}
        />

        <select
          value={fileTypeFilter}
          onChange={(e) => setFileTypeFilter(e.target.value as FileTypeFilter)}
          style={{
            padding: '12px',
            backgroundColor: 'rgba(55, 65, 81, 0.5)',
            border: '1px solid #4b5563',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
          }}
        >
          <option value="all">All File Types</option>
          <option value="images">Images</option>
          <option value="pdfs">PDFs</option>
          <option value="documents">Documents</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          style={{
            padding: '12px',
            backgroundColor: 'rgba(55, 65, 81, 0.5)',
            border: '1px solid #4b5563',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
          }}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="name-asc">Name A-Z</option>
          <option value="name-desc">Name Z-A</option>
        </select>
      </div>

      {/* Materials Grid */}
      {filteredMaterials.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: 'rgba(31, 41, 55, 0.3)',
          borderRadius: '12px',
          border: '1px dashed #4b5563',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
          <h3 style={{ fontSize: '18px', color: '#ffffff', marginBottom: '8px' }}>
            No materials found
          </h3>
          <p style={{ color: '#ffffff', fontSize: '14px' }}>
            {searchQuery || fileTypeFilter !== 'all' || selectedCategory
              ? 'Try adjusting your filters or search query'
              : 'No materials have been uploaded yet'}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '20px',
        }}>
          {filteredMaterials.map(material => (
            <MaterialCard
              key={material.id}
              material={material}
              isAdmin={false}
              onDownload={handleDownload}
              onPreview={handlePreview}
            />
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewMaterial && previewUrl && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => {
            setShowPreviewModal(false);
            setPreviewMaterial(null);
            setPreviewUrl(null);
          }}
        >
          <div
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              backgroundColor: '#1f2937',
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #374151',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <h3 style={{ fontSize: '18px', color: '#ffffff', marginBottom: '4px' }}>
                  {previewMaterial.title}
                </h3>
                <p style={{ fontSize: '13px', color: '#ffffff' }}>
                  {formatFileSize(previewMaterial.file_size)} • {previewMaterial.file_type.split('/')[1].toUpperCase()}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewMaterial(null);
                  setPreviewUrl(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0 8px',
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}>
              {previewMaterial.file_type.startsWith('image/') ? (
                <img
                  src={previewUrl}
                  alt={previewMaterial.title}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : previewMaterial.file_type === 'application/pdf' ? (
                <iframe
                  src={previewUrl}
                  style={{
                    width: '100%',
                    height: '70vh',
                    border: 'none',
                  }}
                  title={previewMaterial.title}
                />
              ) : null}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #374151',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => handleDownload(previewMaterial)}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#d4af37',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                Download
              </button>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewMaterial(null);
                  setPreviewUrl(null);
                }}
                style={{
                  padding: '10px 24px',
                  backgroundColor: 'rgba(55, 65, 81, 0.5)',
                  color: '#ffffff',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsLibrary;


